/**
 * The crowd, as a rule rather than as a set of meshes.
 *
 * Heat has been a number two fighters carry around. This is the first thing
 * that reads it and does something with it, and the thing it does is the most
 * literal one available: the room gets louder.
 *
 * What lives here is **how loud**, and nothing about what that looks like.
 * `src/arenas/crowd.js` turns one number between 0 and 1 into bodies bobbing
 * about, and it is welcome to change its mind about how without anything in
 * here moving. The split is worth keeping for the usual reason — this module
 * runs in `node` with no renderer, and the question "should the crowd be going
 * mad right now" is exactly the kind of thing that ought to be testable.
 *
 * **Whose heat?** The loudest of them. A crowd does not average two fighters;
 * one of them being worth watching is enough to be on your feet for, and a
 * fight where one man is electric and the other is dull is not a quiet night.
 *
 * And then they reach over the barrier. A crowd that only bobs is scenery; the
 * moment it can hold somebody down it is a **stake**, and the whole of heat
 * stops being a number in the corner of the overlay. See `interference` below.
 *
 * They also throw things in. Same shape, same thresholds, its own cooldown: the
 * crowd arms the fighter it likes, or the *opponent* of the fighter it does not.
 * It lands on the floor rather than in a hand, because a favour you have to walk
 * over and pick up is a favour the other man can take off you.
 *
 * Pure. No three.js.
 */

import CROWD from './crowd.json';
import { sampleCurve, validateCurve } from './heat.js';
import { HEAT } from './heat.js';
import { createWeapon, dropSpot, weaponIds } from './weapons.js';

export { CROWD };

export class CrowdConfigError extends Error {}

export function validateCrowd(config = CROWD, max = HEAT.max) {
  const arming = config.weapons;
  if (arming) {
    if (!(arming.coldBelow < arming.hotAbove)) {
      throw new CrowdConfigError('the crowd would arm everybody: coldBelow is not below hotAbove');
    }
    if (!(arming.cooldown > 0)) {
      throw new CrowdConfigError(`weapons.cooldown must be positive, got ${arming.cooldown}`);
    }
    if (!(arming.delay >= 0)) {
      throw new CrowdConfigError(`weapons.delay cannot be negative, got ${arming.delay}`);
    }
    if (!Array.isArray(arming.offer) || arming.offer.length === 0) {
      throw new CrowdConfigError('the crowd has nothing to offer');
    }
    const known = weaponIds();
    for (const id of arming.offer) {
      if (!known.includes(id)) throw new CrowdConfigError(`the crowd cannot offer "${id}"`);
    }
  }

  const rules = config.interference;
  if (rules) {
    if (!(rules.coldBelow < rules.hotAbove)) {
      throw new CrowdConfigError('the crowd would turn on everybody: coldBelow is not below hotAbove');
    }
    for (const field of ['duration', 'cooldown', 'reach']) {
      if (!(rules[field] > 0)) {
        throw new CrowdConfigError(`interference.${field} must be positive, got ${rules[field]}`);
      }
    }
    if (!(rules.cooldown > rules.duration)) {
      // Otherwise the hands are back before they have let go, which is not a
      // cooldown, it is a permanent state of being held.
      throw new CrowdConfigError('interference.cooldown must outlast its own duration');
    }
  }
  validateCurve(config.energy?.curve, max);
  const energy = config.energy.curve.map(([, value]) => value);
  if (energy.some((value) => value > 1)) {
    throw new CrowdConfigError('crowd energy is a fraction; it cannot exceed 1');
  }
  for (const [name, range] of Object.entries(config.motion ?? {})) {
    if (!Array.isArray(range) || range.length !== 2) {
      throw new CrowdConfigError(`motion.${name} must be a [quiet, loud] pair`);
    }
    if (!(range[1] >= range[0])) {
      throw new CrowdConfigError(`motion.${name} goes down as the room gets louder`);
    }
  }
  return config;
}

/**
 * `fighters` are whoever is in the ring. Each needs an `id` and a `heat`; the
 * crowd reads nothing else off them in this module. `ground` is the floor
 * weapons land on — without one the crowd simply never throws anything in.
 */
export function createCrowd({ fighters = [], ground = null, ...overrides } = {}) {
  const config = Object.freeze({
    ...CROWD,
    ...overrides,
    energy: Object.freeze({ ...CROWD.energy, ...overrides.energy }),
    motion: Object.freeze({ ...CROWD.motion, ...overrides.motion }),
    spacing: Object.freeze({ ...CROWD.spacing, ...overrides.spacing }),
    interference: Object.freeze({ ...CROWD.interference, ...overrides.interference }),
    weapons: Object.freeze({ ...CROWD.weapons, ...overrides.weapons }),
  });
  validateCrowd(config);

  let watching = [...fighters];
  /** What each fighter was doing last frame, so a knockdown can be *noticed*. */
  const before = new Map();
  /** The grab currently in progress, or null. */
  let grab = null;
  /** Seconds until the hands are allowed back over the barrier. */
  let cooling = 0;
  const grabListeners = new Set();
  /**
   * Seconds until the crowd will throw something in again. Starts at `delay`
   * rather than zero — a bat at the opening bell is not a crowd reacting to a
   * fight, it is a fight that started with a bat.
   */
  let armingCooling = config.weapons?.delay ?? 0;
  const offerListeners = new Set();
  let offers = 0;

  /** Who else is in. Two fighters, so this is the other one. */
  function otherThan(fighter) {
    return watching.find((other) => other !== fighter) ?? null;
  }

  /**
   * Whether the crowd gets involved with a fighter who has just gone down, and
   * why.
   *
   * Two reasons, and they are the two halves of the same sentence. They hold
   * somebody down **for** a fighter they like, and they hold somebody down
   * **because** they do not like them. From the player's chair those read as
   * two different mechanics — a reward at high heat and a punishment at low —
   * and they are one rule with the thresholds at either end of the meter.
   *
   * Liking the other fighter wins when both are true, because the crowd is
   * doing somebody a favour rather than settling a score.
   */
  function reasonToGrab(down) {
    const rules = config.interference;
    const rival = otherThan(down);
    const favouring = (rival?.heat?.value ?? 0) >= rules.hotAbove;
    const disliked = (down?.heat?.value ?? Infinity) <= rules.coldBelow;
    if (favouring) return { reason: 'favouring', favouring, disliked, forWhom: rival };
    if (disliked) return { reason: 'disliked', favouring, disliked, forWhom: rival };
    return null;
  }

  /**
   * Who the crowd would arm right now, and why.
   *
   * The same sentence as `reasonToGrab`, pointed the other way round: the crowd
   * hands a weapon **to** the fighter it likes, and to the **opponent** of the
   * one it does not. Both are a favour; only the recipient differs.
   */
  function reasonToArm() {
    const rules = config.weapons;
    if (!rules) return null;
    const best = hottest();
    if (best && best.value >= rules.hotAbove) {
      return { reason: 'favouring', forWhom: best.fighter, against: otherThan(best.fighter) };
    }
    // Nobody is loved. Is anybody actively disliked?
    const worst = watching.reduce(
      (low, fighter) =>
        Number.isFinite(fighter?.heat?.value) && (!low || fighter.heat.value < low.heat.value)
          ? fighter
          : low,
      null
    );
    if (worst && worst.heat.value <= rules.coldBelow) {
      const rival = otherThan(worst);
      if (rival) return { reason: 'disliked', forWhom: rival, against: worst };
    }
    return null;
  }

  /** The hottest fighter's heat, or nothing to watch. */
  function hottest() {
    let best = null;
    for (const fighter of watching) {
      const value = fighter?.heat?.value;
      if (!Number.isFinite(value)) continue;
      if (!best || value > best.value) best = { value, fighter };
    }
    return best;
  }

  return {
    config,

    /** Who is being watched. Re-pointed when the fight is rebuilt. */
    get fighters() {
      return watching;
    },
    watch(next = []) {
      watching = [...next];
      return watching;
    },

    /** The heat the crowd is reacting to: the loudest fighter's. */
    get heat() {
      return hottest()?.value ?? 0;
    },
    /** And whose it is, which step 40 needs and the overlay likes. */
    get favourite() {
      return hottest()?.fighter ?? null;
    },

    /**
     * How animated the room is, 0 to 1.
     *
     * Not the same shape as the momentum drive and deliberately so: that one is
     * near-linear because it is a payout, and this one is flat at the bottom
     * and steep through the middle because a crowd does not warm up gradually.
     * It sits there, and then it is on its feet.
     */
    get energy() {
      return sampleCurve(config.energy.curve, this.heat);
    },

    /**
     * A named motion parameter at the current energy. The arena asks for these
     * by name rather than reading the table, so the tuning can grow a knob
     * without the renderer learning about it.
     */
    motion(name) {
      const range = config.motion[name];
      if (!range) return 0;
      const [quiet, loud] = range;
      return quiet + (loud - quiet) * this.energy;
    },

    /** Everything the arena needs for a frame, in one read. */
    get mood() {
      const energy = this.energy;
      return Object.freeze({
        heat: this.heat,
        energy,
        bob: this.motion('bob'),
        sway: this.motion('sway'),
        rate: this.motion('rate'),
        lift: this.motion('lift'),
        lean: this.motion('lean'),
      });
    },

    // --- reaching over the barrier ------------------------------------------

    /** Who is being held, and why, or null. Frozen; safe to hold onto. */
    get grab() {
      return grab;
    },
    get holding() {
      return Boolean(grab);
    },
    /** Seconds until the hands are allowed back. Zero when they are ready. */
    get cooling() {
      return cooling;
    },

    /** The floor weapons land on, so a reader does not need it passed twice. */
    get ground() {
      return ground;
    },
    /** Seconds until the crowd will throw something in again. */
    get armingCooling() {
      return armingCooling;
    },
    /** How many weapons it has thrown in this round. */
    get offers() {
      return offers;
    },
    /** Fires when a weapon lands on the floor, with who it was meant for. */
    onOffer(listener) {
      offerListeners.add(listener);
      return () => offerListeners.delete(listener);
    },

    /** Fires once when the crowd takes hold of somebody, and once on release. */
    onGrab(listener) {
      grabListeners.add(listener);
      return () => grabListeners.delete(listener);
    },

    /**
     * One frame of the crowd's own business.
     *
     * A fighter going down is *noticed* rather than reported, by comparing this
     * frame's state against last frame's. Nothing else in the game fires an
     * event for a knockdown, and having the crowd ask for one would mean
     * `fight.js` growing a listener whose only subscriber is a decoration.
     */
    update(dt) {
      if (!(dt > 0)) return grab;
      if (cooling > 0) cooling = Math.max(0, cooling - dt);
      if (armingCooling > 0) armingCooling = Math.max(0, armingCooling - dt);
      else offer();

      if (grab) {
        grab = Object.freeze({ ...grab, left: grab.left - dt });
        if (grab.left <= 0) {
          const finished = grab;
          grab = null;
          // Let go early if the round ended under them, but never leave a
          // fighter pinned: the hold is the crowd's, releasing it is too.
          finished.fighter?.machine?.release?.();
          for (const listener of grabListeners) {
            listener({ ...finished, held: false, released: true });
          }
        }
      }

      for (const fighter of watching) {
        const state = fighter?.machine?.state ?? null;
        const was = before.get(fighter) ?? null;
        before.set(fighter, state);
        if (state !== 'knockdown' || was === 'knockdown') continue;
        consider(fighter);
      }
      return grab;
    },

    /** Lets go of whoever is being held, and starts the cooldown anyway. */
    release() {
      if (!grab) return false;
      const finished = grab;
      grab = null;
      finished.fighter?.machine?.release?.();
      for (const listener of grabListeners) {
        listener({ ...finished, held: false, released: true });
      }
      return true;
    },

    reset() {
      if (grab) grab.fighter?.machine?.release?.();
      grab = null;
      cooling = 0;
      armingCooling = config.weapons?.delay ?? 0;
      offers = 0;
      before.clear();
    },
  };

  /**
   * Throws a weapon in, if there is anybody to throw one to.
   *
   * It lands on the **floor**, near the recipient and away from their opponent,
   * rather than in a hand. A favour you have to turn your back and walk to is a
   * favour the other man gets a say in, and `ground` hands it to whoever
   * actually reaches it — which may well not be who it was thrown for.
   *
   * Does nothing without a `ground` to drop it on: an arena with no floor state
   * simply has no weapons in it, and the crowd is none the wiser.
   */
  function offer() {
    const rules = config.weapons;
    if (!rules || !ground) return null;
    const why = reasonToArm();
    if (!why) return null;

    armingCooling = rules.cooldown;
    offers += 1;
    // Round-robin rather than random, so a round's weapons are reproducible and
    // a session does not hand out three bats in a row by luck.
    const id = rules.offer[(offers - 1) % rules.offer.length];
    const weapon = createWeapon(id);
    const at = dropSpot(why.forWhom, why.against, rules.distance);
    const item = ground.drop(weapon, at, { forWhom: why.forWhom?.id ?? null, reason: why.reason });

    const event = {
      id,
      weapon,
      forWhom: why.forWhom?.id ?? null,
      against: why.against?.id ?? null,
      reason: why.reason,
      at,
    };
    for (const listener of offerListeners) listener(event);
    return item;
  }

  /**
   * Somebody just hit the floor. Does the crowd get involved?
   *
   * Three gates, and the cooldown is the one that matters most: without it the
   * crowd would take hold on every single knockdown of a hot round, which stops
   * being a moment and becomes a rule about how knockdowns work.
   */
  function consider(down) {
    const rules = config.interference;
    if (!rules || grab || cooling > 0) return null;
    const why = reasonToGrab(down);
    if (!why) return null;

    cooling = rules.cooldown;
    // The hold *is* the window, and it is the only thing the crowd does. A
    // fighter who cannot get up is a fighter who can be hit, and a jab against
    // one lands for real — measured at 7.3 wellness.
    //
    // It deliberately does **not** open the `stunned` window the wall finisher
    // is gated on. That was tried: it offers `slam` on the grapple button, and
    // a grapple cannot reach somebody lying on the floor — the hitbox is at
    // chest height and the target is not. Offering a finisher that whiffs every
    // time is worse than offering nothing.
    down.machine?.hold?.(rules.duration);

    grab = Object.freeze({
      fighter: down,
      id: down.id ?? null,
      forWhom: why.forWhom?.id ?? null,
      reason: why.reason,
      favouring: why.favouring,
      disliked: why.disliked,
      duration: rules.duration,
      left: rules.duration,
    });
    for (const listener of grabListeners) listener({ ...grab, held: true, released: false });
    return grab;
  }
}
